import {
  DateTime,
  type DateTimeFormatOptions,
  type DateTimeOptions,
} from "luxon";
import { getBrowserIanaTimeZone } from "./browser-time-zone";

/** BCP 47 locale for portal date/time display (browser default; override later if CRM locale is added). */
export function getPortalLocale(): string {
  if (typeof navigator !== "undefined" && navigator.language) {
    return navigator.language;
  }
  return "en";
}

function localized(dt: DateTime, format: DateTimeFormatOptions): string {
  return dt.setLocale(getPortalLocale()).toLocaleString(format);
}

function zonedFromIso(
  iso: string,
  zone: string = getBrowserIanaTimeZone(),
): DateTime | null {
  if (!iso?.trim()) {
    return null;
  }
  const dt = DateTime.fromISO(iso, { zone: "utc" }).setZone(zone);
  return dt.isValid ? dt : null;
}

function zonedFromIsoWithParseOptions(
  iso: string,
  parseOptions: DateTimeOptions = { zone: "utc" },
): DateTime | null {
  if (!iso?.trim()) {
    return null;
  }
  const dt = DateTime.fromISO(iso, parseOptions).setZone(
    getBrowserIanaTimeZone(),
  );
  return dt.isValid ? dt : null;
}

function nowZoned(
  nowMs: number = Date.now(),
  zone: string = getBrowserIanaTimeZone(),
): DateTime {
  return DateTime.fromMillis(nowMs, { zone });
}

/** Date only — locale order and separators (e.g. US M/D/YY, AU D/M/YY). */
export function formatPortalDate(
  iso: string | null | undefined,
  parseOptions: DateTimeOptions = { zone: "utc" },
): string {
  const dt = zonedFromIsoWithParseOptions(String(iso ?? ""), parseOptions);
  if (!dt) {
    return "";
  }
  return localized(dt, DateTime.DATE_SHORT);
}

/** Calendar date from a UTC ISO datetime — no shift to browser time zone. */
export function formatPortalUtcCalendarDate(
  iso: string | null | undefined,
  parseOptions: DateTimeOptions = { zone: "utc" },
): string {
  if (!iso?.trim()) {
    return "";
  }
  const dt = DateTime.fromISO(iso, parseOptions);
  if (!dt.isValid) {
    return "";
  }
  return localized(dt, DateTime.DATE_SHORT);
}

/** Medium date (month abbreviation, day, year). */
export function formatPortalDateMed(iso: string | null | undefined): string {
  const dt = zonedFromIso(String(iso ?? ""));
  if (!dt) {
    return "";
  }
  return localized(dt, DateTime.DATE_MED);
}

/** Date and time in the user's locale and time zone. */
export function formatPortalDateTime(iso: string | null | undefined): string {
  const dt = zonedFromIso(String(iso ?? ""));
  if (!dt) {
    return "";
  }
  return localized(dt, DateTime.DATETIME_MED);
}

/** Time only (locale 12h/24h). */
export function formatPortalTime(iso: string | null | undefined): string {
  const dt = zonedFromIso(String(iso ?? ""));
  if (!dt) {
    return "";
  }
  return localized(dt, DateTime.TIME_SIMPLE);
}

/**
 * Absolute timestamp with today / yesterday when on the same calendar day.
 * Used by ticket and project list/detail labels.
 */
export function formatPortalDateWithTodayYesterday(
  iso: string | null | undefined,
  prependText: string = "",
): string {
  if (iso == null) {
    return "";
  }
  const at = zonedFromIso(iso);
  if (!at) {
    return "";
  }

  const now = nowZoned();
  const yesterday = now.minus({ days: 1 });
  const time = localized(at, DateTime.TIME_SIMPLE);

  if (at.hasSame(now, "day")) {
    return `today at ${time}`;
  }
  if (at.hasSame(yesterday, "day")) {
    return `yesterday at ${time}`;
  }
  return `${prependText}${localized(at, DateTime.DATE_MED)} at ${time}`;
}

function formatPortalWeekdayLong(dt: DateTime): string {
  return localized(dt, { weekday: "long" });
}

function formatPortalMonthDay(dt: DateTime): string {
  return localized(dt, { month: "short", day: "numeric" });
}

function formatPortalMonthDayYear(dt: DateTime): string {
  return localized(dt, { month: "short", day: "numeric", year: "numeric" });
}

function formatPortalWeekdayMonthDayYear(dt: DateTime): string {
  return localized(dt, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/** Calendar days between `at` and `now` (0 = today, 1 = yesterday). */
export function portalCalendarDayDiff(at: DateTime, now: DateTime): number {
  return Math.floor(now.startOf("day").diff(at.startOf("day"), "days").days);
}

export type PortalCalendarDueDateView = {
  dueDateLabel: string;
  dueDateOverdue?: boolean;
  isDueToday?: boolean;
  isDueTomorrow?: boolean;
};

export function buildPortalCalendarDueDateView(
  dueDate: string | null | undefined,
  options: { emptyLabel?: string; invalidLabel?: string } = {},
): PortalCalendarDueDateView {
  const emptyLabel = options.emptyLabel ?? "No target date";
  const invalidLabel = options.invalidLabel ?? "";

  if (!dueDate) {
    return { dueDateLabel: emptyLabel };
  }

  const at = zonedFromIso(dueDate);
  if (!at) {
    return { dueDateLabel: invalidLabel || dueDate };
  }

  const now = nowZoned();
  const dayDiff = portalCalendarDayDiff(at, now);

  if (dayDiff === -1) {
    return { dueDateLabel: "Tomorrow", isDueTomorrow: true };
  }

  return {
    dueDateLabel: formatPortalChatCalendarDay(dueDate),
    dueDateOverdue: dayDiff > 0,
    isDueToday: dayDiff === 0,
  };
}

/** Chat date separator pill (calendar day). */
export function formatPortalChatCalendarDay(
  iso: string | null | undefined,
  nowMs: number = Date.now(),
): string {
  const at = zonedFromIso(String(iso ?? ""));
  const now = nowZoned(nowMs);
  if (!at) {
    return "";
  }

  const diff = portalCalendarDayDiff(at, now);

  if (diff === 0) {
    return "Today";
  }
  if (diff === 1) {
    return "Yesterday";
  }
  if (diff >= 2 && diff <= 6) {
    return formatPortalWeekdayLong(at);
  }
  if (at.year === now.year) {
    return formatPortalMonthDay(at);
  }
  return formatPortalMonthDayYear(at);
}

/** Hover on chat date separator pill. */
export function formatPortalChatCalendarDayHover(
  iso: string | null | undefined,
): string {
  const at = zonedFromIso(String(iso ?? ""));
  if (!at) {
    return "";
  }
  return formatPortalWeekdayMonthDayYear(at);
}

/** Chat message timestamp (timeline bubbles / incoming header). */
export function formatPortalChatMessageTimestamp(
  iso: string | null | undefined,
  nowMs: number = Date.now(),
): string {
  const at = zonedFromIso(String(iso ?? ""));
  const now = nowZoned(nowMs);
  if (!at) {
    return "";
  }

  const diff = portalCalendarDayDiff(at, now);
  const time = formatPortalTime(iso);

  if (diff === 0) {
    return time;
  }
  if (diff === 1) {
    return `Yesterday at ${time}`;
  }
  if (diff >= 2 && diff <= 6) {
    return `${formatPortalWeekdayLong(at)} at ${time}`;
  }
  if (at.year === now.year) {
    return `${formatPortalMonthDay(at)} at ${time}`;
  }
  return `${formatPortalMonthDayYear(at)} at ${time}`;
}

/** Hover on chat message timestamp. */
export function formatPortalChatMessageTimestampHover(
  iso: string | null | undefined,
): string {
  const at = zonedFromIso(String(iso ?? ""));
  if (!at) {
    return "";
  }
  const time = formatPortalTime(iso);
  return `${formatPortalWeekdayMonthDayYear(at)} at ${time}`;
}

/**
 * Inbox / ticket list rolling activity time.
 * `nowMs` supports tests and the sidebar minute tick.
 */
export function formatPortalSidebarListTime(
  iso: string | null | undefined,
  nowMs: number = Date.now(),
): string {
  const at = zonedFromIso(String(iso ?? ""));
  const now = nowZoned(nowMs);
  if (!at) {
    return "";
  }

  const diffMs = now.toMillis() - at.toMillis();

  if (diffMs < 0) {
    if (Math.abs(diffMs) < 60_000) {
      return "Just now";
    }
    if (at.hasSame(now, "day")) {
      return "Today";
    }
    if (at.hasSame(now.minus({ days: 1 }), "day")) {
      return "Yesterday";
    }
    if (at.year === now.year) {
      return formatPortalMonthDay(at);
    }
    return formatPortalMonthDayYear(at);
  }

  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) {
    return "Just now";
  }
  if (diffSec < 3600) {
    const mins = Math.max(1, Math.floor(diffSec / 60));
    return `${mins}m ago`;
  }
  if (diffSec < 86_400) {
    const hrs = Math.max(1, Math.floor(diffSec / 3600));
    return `${hrs}h ago`;
  }
  if (diffSec < 172_800) {
    return "Yesterday";
  }
  if (diffSec < 604_800) {
    return formatPortalWeekdayLong(at);
  }
  if (at.year === now.year) {
    return formatPortalMonthDay(at);
  }
  return formatPortalMonthDayYear(at);
}

/** Hover on inbox / ticket list timestamp (same as chat message hover). */
export function formatPortalSidebarListTimeHover(
  iso: string | null | undefined,
): string {
  return formatPortalChatMessageTimestampHover(iso);
}

/**
 * Format a calendar date stored as `yyyy-MM-dd` (no time) for display.
 * Used by form preview fields that keep ISO day strings internally.
 */
export function formatPortalCalendarDayString(
  yyyyMmDd: string | null | undefined,
): string {
  if (!yyyyMmDd?.trim()) {
    return "";
  }
  const dt = DateTime.fromFormat(yyyyMmDd.trim(), "yyyy-MM-dd", {
    zone: "local",
  });
  if (!dt.isValid) {
    return "";
  }
  return localized(dt, DateTime.DATE_SHORT);
}
